// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useState } from "react";

import { mdiDotsVertical } from "@mdi/js";
import { Button, Popover, Tooltip } from "antd";

import { SHAPEFILE_SOURCE } from "components/project/layers/hooks";

import { ActionListWrapper } from "..";
import { ClearSelectedItemsAction } from "../actions/ClearSelectedItemsAction";
import { ContextMenuPopover } from "../context-menu/ContextMenuPopover";
import { UseRightClickContextMenuReturn } from "../hooks/useRightClickContextMenu";
import { ProjectLinkedShapefileFolderColorPicker } from "../shapefile-color-picker/ProjectLinkedShapefileFolderColorPicker";
import { ProjectShapefileFolderColorPicker } from "../shapefile-color-picker/ProjectShapefileFolderColorPicker";
import "./ShapefileFolderActions.scss";
import {
  ShapefileFolderDeleteAction,
  ShapefileFolderDeleteSelectedItemsAction,
  ShapefileFolderRemoveAction,
  ShapefileFolderRenameAction,
  ShapefileFolderSelectItemsAction,
  ShapefileFolderSortAction
} from "./actions";
import { ShapefileFolderAddAction } from "./actions/ShapefileFolderAddAction";
import { ShapefileFolderExportAction } from "./actions/ShapefileFolderExportAction";

interface IShapefileFolderActionsProps {
  addFolder?: () => void;
  deleteFolder?: () => void;
  hasColorPicker?: boolean;
  hasSelectItems?: boolean;
  isDisabled?: boolean;
  removeFolder?: () => void;
  setIsRenaming?: () => void;
  shapefileNodeId?: string;
  sortShapefilesAscend?: () => void;
  sortShapefilesDescend?: () => void;
  canExport?: boolean;
  isMcDan?: boolean;
  isOrg?: boolean;
  projectId?: string;
  folderName: string;
  deleteSelectedItemsActionText?: string;
  contextMenu?: UseRightClickContextMenuReturn;
  place?: "top" | "right" | "bottom" | "left";
}

/**
 * Essentially a wrapper for the shapefile actions, so that the actions can be used in different contexts.
 * @param params
 * @returns
 */
export function ShapefileFolderActions(
  params: IShapefileFolderActionsProps
): JSX.Element {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const hasAnyActions = !!(
    params.addFolder ||
    params.hasSelectItems ||
    params.hasColorPicker ||
    params.setIsRenaming ||
    params.deleteFolder ||
    params.removeFolder ||
    params.sortShapefilesAscend ||
    params.sortShapefilesDescend ||
    params.canExport
  );

  if (!hasAnyActions) {
    return null;
  }
  const FolderColorPicker = params.isOrg ? (
    <ProjectLinkedShapefileFolderColorPicker
      shapefileNodeId={params.shapefileNodeId}
      type={params.isOrg ? SHAPEFILE_SOURCE.ORG : SHAPEFILE_SOURCE.MCDAN}
    />
  ) : (
    <ProjectShapefileFolderColorPicker
      shapefileNodeId={params.shapefileNodeId}
      projectId={params.projectId}
      place={params.place}
    />
  );
  const actionList = (
    <ActionListWrapper data-testid="shapefile-folder-context-menu">
      {params.addFolder && (
        <ShapefileFolderAddAction
          addFolder={() => {
            params.addFolder();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          setPopoverOpen={setIsPopoverOpen}
        />
      )}

      {params.hasColorPicker && FolderColorPicker}

      {params.sortShapefilesAscend && (
        <ShapefileFolderSortAction
          setPopoverOpen={(isOpen) => {
            setIsPopoverOpen(isOpen);
            if (!isOpen && params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          sortType="ascend"
          sortShapefiles={() => {
            params.sortShapefilesAscend();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
        />
      )}

      {params.sortShapefilesDescend && (
        <ShapefileFolderSortAction
          setPopoverOpen={(isOpen) => {
            setIsPopoverOpen(isOpen);
            if (!isOpen && params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          sortType="descend"
          sortShapefiles={() => {
            params.sortShapefilesDescend();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
        />
      )}
      {params.setIsRenaming && (
        <ShapefileFolderRenameAction
          setIsRenaming={() => {
            params.setIsRenaming();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
        />
      )}

      {params.shapefileNodeId && params.canExport && (
        <ShapefileFolderExportAction
          shapefileNodeId={params.shapefileNodeId}
          closePopover={() => {
            setIsPopoverOpen(false);
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          isMcDan={params.isMcDan}
          folderName={params.folderName}
        />
      )}

      {params.hasSelectItems && (
        <ShapefileFolderSelectItemsAction
          setPopoverOpen={setIsPopoverOpen}
          onContextMenuClose={params.contextMenu?.onContextMenuClose}
        />
      )}

      {params.hasSelectItems && (
        <ClearSelectedItemsAction
          onContextMenuClose={params.contextMenu?.onContextMenuClose}
        />
      )}

      {params.deleteFolder && (
        <ShapefileFolderDeleteAction
          deleteShapefile={() => {
            params.deleteFolder();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
        />
      )}

      {params.removeFolder && (
        <ShapefileFolderRemoveAction
          removeShapefile={() => {
            params.removeFolder();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
        />
      )}

      {params.hasSelectItems && (
        <ShapefileFolderDeleteSelectedItemsAction
          onContextMenuClose={params.contextMenu?.onContextMenuClose}
          actionText={params.deleteSelectedItemsActionText}
        />
      )}
    </ActionListWrapper>
  );

  const contextMenuPopover = (
    <ContextMenuPopover actionList={actionList} contextMenu={params.contextMenu} />
  );

  const actionButton = (
    <Tooltip title="Actions">
      <Popover
        arrowPointAtCenter={true}
        content={actionList}
        onOpenChange={(isOpen) => {
          if (!params.isDisabled) {
            setIsPopoverOpen(isOpen);
          }
        }}
        open={isPopoverOpen}
        overlayClassName="popover-no-padding"
        placement="bottomLeft"
        trigger="click">
        <Button
          data-testid="shapefile-folder-actions-button"
          disabled={params.isDisabled}
          type="text"
          aria-label="Edit">
          <Icon path={mdiDotsVertical} size={"21px"} />
        </Button>
      </Popover>
    </Tooltip>
  );

  return (
    <>
      {contextMenuPopover}
      {actionButton}
    </>
  );
}
